/* My Home Plant: Essenify images, matched by product handle (not image URL).
 * Only loaded on essenify-bundle. No app state, cart or price changes.
 * One cached request per displayed product; at most four in flight.
 */
(() => {
  'use strict';
  const cache = new Map();
  const queue = [];
  let active = 0;
  const mounted = new WeakSet();
  const replacements = new WeakMap();

  function pump() {
    while (active < 4 && queue.length) {
      active++;
      const job = queue.shift();
      job().finally(() => { active--; pump(); });
    }
  }

  function preload(src) {
    return new Promise(resolve => {
      const image = new Image();
      const timer = setTimeout(() => finish(null), 8000);
      const finish = value => {
        clearTimeout(timer);
        image.onload = image.onerror = null;
        resolve(value);
      };
      image.onload = () => finish(src);
      image.onerror = () => finish(null);
      image.src = src;
    });
  }

  function getImage(handle) {
    if (cache.has(handle)) return cache.get(handle);
    const promise = new Promise(resolve => {
      queue.push(async () => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 8000);
        try {
          const root = window.Shopify?.routes?.root || '/';
          const url = new URL(`${root}products/${encodeURIComponent(handle)}`, location.origin);
          url.searchParams.set('view', 'mhp-bundle-image');
          const preview = new URL(location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) throw new Error('Image lookup failed');
          const data = await response.json();
          if (data.handle !== handle || !data.image) { resolve(null); return; }
          const imageURL = new URL(data.image, location.origin);
          if (imageURL.protocol !== 'https:') { resolve(null); return; }
          resolve(await preload(imageURL.href));
        } catch (_) {
          // Includes missing view, missing metafield, network errors and invalid JSON.
          resolve(null);
        } finally { clearTimeout(timer); }
      });
    });
    cache.set(handle, promise);
    pump();
    return promise;
  }

  function cardHandle(card) {
    const link = card.querySelector('.essenify-mam-product-title a[href]');
    if (!link) return null;
    try {
      const url = new URL(link.getAttribute('href'), location.href);
      if (url.origin !== location.origin) return null;
      const match = url.pathname.match(/\/products\/([^/]+)\/?$/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (_) { return null; }
  }

  function mount() {
    const wrapper = document.getElementById('essenify-mam-bundle-wrapper');
    if (!wrapper || mounted.has(wrapper)) return;
    mounted.add(wrapper);
    window.mhpBundleImageGuardActive = true;
    const variants = new Map();
    let scheduled = false;

    function replace(image, handle, currentHandle) {
      if (!image) return;
      if (!handle) {
        image.setAttribute('data-mhp-image-ready', '');
        return;
      }
      // Already replaced: no additional promise or DOM write on unrelated changes.
      const previous = replacements.get(image);
      if (previous?.handle === handle && image.getAttribute('src') === previous.src) return;
      image.removeAttribute('data-mhp-image-ready');
      getImage(handle).then(async src => {
        if (!wrapper.contains(image) || currentHandle() !== handle) return;
        if (src) {
          if (image.getAttribute('src') !== src) image.setAttribute('src', src);
          // Wait for this DOM image to be decoded, not just the preload image.
          if (typeof image.decode === 'function') {
            try {
              await Promise.race([image.decode(), new Promise(resolve => setTimeout(resolve, 3000))]);
            } catch (_) { /* The preloaded image remains the preferred source. */ }
          }
          if (!wrapper.contains(image) || currentHandle() !== handle || image.getAttribute('src') !== src) return;
          replacements.set(image, { handle, src });
        }
        // Missing/failed metafield: reveal the untouched Shopify image as well.
        image.setAttribute('data-mhp-image-ready', '');
      });
    }

    function slotHandle(slot) {
      const key = slot.querySelector('[data-variant-id]')?.getAttribute('data-variant-id');
      return key ? variants.get(key.split('_')[0]) : null;
    }

    function refresh() {
      scheduled = false;
      variants.clear();
      wrapper.querySelectorAll('.essenify-mam-product-card').forEach(card => {
        const handle = cardHandle(card);
        card.querySelectorAll('.essenify-mam-variant-select option').forEach(option => {
          variants.set(option.value, handle);
        });
        replace(card.querySelector('img.essenify-mam-product-image'), handle, () => cardHandle(card));
      });
      wrapper.querySelectorAll('.essenify-mam-slot.filled').forEach(slot => {
        replace(slot.querySelector('img.essenify-mam-slot-img'), slotHandle(slot), () => slotHandle(slot));
      });
    }

    function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(refresh);
    }

    new MutationObserver(records => {
      // Hide a previously released image immediately when Essenify changes src.
      // Mutation callbacks run before painting; the batched refresh follows.
      records.forEach(record => {
        if (record.type !== 'attributes') return;
        const previous = replacements.get(record.target);
        if (previous && record.target.getAttribute('src') !== previous.src) {
          record.target.removeAttribute('data-mhp-image-ready');
        }
      });
      if (records.some(record => record.type === 'attributes'
        ? record.target.matches('img.essenify-mam-product-image, img.essenify-mam-slot-img')
        : [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1))) schedule();
    }).observe(wrapper, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
    refresh();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
  document.addEventListener('shopify:section:load', mount);
})();
